import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import RouterPathProvider from "@core/RouterPath/RouterPathProvider";
import { type UseApiOptions, useDeferApi } from "@core-ui/hooks/useApi";
import type LinkItem from "@ext/article/LinkCreator/models/LinkItem";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import type GitBranchData from "@ext/git/core/GitBranch/model/GitBranchData";
import { useEffect } from "react";

// A failed lookup counts as a missing article: the branch is already switched, so the address must still follow it.
const ARTICLE_LOOKUP_OPTS: UseApiOptions = Object.freeze({ consumeError: true });

const useOnPathnameUpdateBranch = () => {
	const router = useRouter();
	const { call: fetchLinkItem } = useDeferApi<LinkItem>({ opts: ARTICLE_LOOKUP_OPTS });

	// biome-ignore lint/correctness/useExhaustiveDependencies: re-subscribed per address so the listener reads the current one
	useEffect(() => {
		const articleExists = async (pathname: string, catalogName: string) =>
			!!(await fetchLinkItem({ url: (api) => api.getLinkItemByPath(pathname, catalogName) }));

		const onUpdateBranch = async (branch: GitBranchData, caller: OnBranchUpdateCaller) => {
			if (
				caller === OnBranchUpdateCaller.MergeRequest ||
				caller === OnBranchUpdateCaller.DiscardNoReset ||
				caller === OnBranchUpdateCaller.Publish ||
				caller === OnBranchUpdateCaller.Init
			)
				return;

			const routerPath = new Path(router.path + router.hash).removeExtraSymbols;
			if (!RouterPathProvider.isEditorPathname(routerPath)) return;

			const pathnameData = RouterPathProvider.parsePath(routerPath);
			const isLocal = RouterPathProvider.isLocal(pathnameData);
			if (isLocal) return;

			const isCheckoutToNewCreatedBranch = caller === OnBranchUpdateCaller.CheckoutToNewCreatedBranch;
			const keepsArticle =
				isCheckoutToNewCreatedBranch ||
				(caller === OnBranchUpdateCaller.Checkout &&
					(await articleExists(router.path, pathnameData.catalogName)));

			const newPath = RouterPathProvider.updatePathnameData(
				pathnameData,
				keepsArticle
					? { refname: branch?.name }
					: { refname: branch?.name, filePath: null, itemLogicPath: null },
			).value;

			if (isCheckoutToNewCreatedBranch) {
				router.setPreventNextPushRefresh(true);
			}

			void router.pushPath(newPath);
		};

		BranchUpdaterService.addListener(onUpdateBranch);
		return () => BranchUpdaterService.removeListener(onUpdateBranch);
	}, [router.path, router.hash, fetchLinkItem]);
};

export default useOnPathnameUpdateBranch;
